/**
 * Powersensor downloads in the web app (admins only). Powersensor is being retired, so
 * this folder is self-contained: see apps/api/src/powersensor/README.md for removing it.
 */
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { Zap } from 'lucide-react';
import { useMe } from '../../api/auth';
import { useDialog, type DialogProps } from '../../components/dialogs/DialogProvider';
import { formatAgo, formatDateTime } from '../../lib/format';
import { colours } from '../../theme';
import { useDriverPowersensor, usePowersensorStatus } from './api';
import { FetchPanel } from './FetchPanel';

const useIsAdmin = () => useMe().data?.role === 'admin';

/** On the driver's Powersensor tab: their latest reading and a download for their account. */
export function DriverPowersensorPanel({ driverId }: { driverId: string }) {
  const isAdmin = useIsAdmin();
  // Refresh the summary when a download finishes.
  const status = usePowersensorStatus(isAdmin);
  const info = useDriverPowersensor(driverId, status.data?.run?.finishedAt ?? null, isAdmin);
  if (!isAdmin) return null;
  const i = info.data;
  return (
    <Box sx={{ borderTop: `1px solid ${colours.hairline}`, pt: 3, display: 'grid', gap: 2 }}>
      <Box>
        <Typography sx={{ fontWeight: 500 }}>Download readings</Typography>
        <Typography variant="body2" color="textSecondary">
          {i
            ? i.lastReadingAt
              ? `Latest reading for ${i.email}: ${formatDateTime(i.lastReadingAt)} (${formatAgo(i.lastReadingAt)}). ${i.last7Days.toLocaleString()} readings in the last 7 days.`
              : `No readings saved for ${i.email} yet.`
            : 'Checking…'}{' '}
          Readings download every few hours by themselves; use this to fill a gap. Save any change to the email first.
        </Typography>
      </Box>
      <FetchPanel driverId={driverId} />
    </Box>
  );
}

/** The button on the Drivers page for admins: download every Powersensor account. */
export function PowersensorButton() {
  const isAdmin = useIsAdmin();
  const dialog = useDialog();
  if (!isAdmin) return null;
  return (
    <Button startIcon={<Zap size={20} strokeWidth={1.75} />} onClick={() => void dialog.open(PowersensorDialog, {})}>
      Powersensor
    </Button>
  );
}

function PowersensorDialog({ close }: DialogProps) {
  return (
    <Dialog open onClose={() => close()} fullWidth maxWidth="sm" aria-labelledby="powersensor-title">
      <DialogTitle id="powersensor-title">Download Powersensor readings</DialogTitle>
      <DialogContent sx={{ display: 'grid', gap: 3 }}>
        <Typography color="textSecondary">
          Downloads every account shared with Infinity EV's Powersensor account, one after another, between the two dates. Readings
          also download every few hours by themselves; use this to fill a gap. It carries on if you close this window.
        </Typography>
        <FetchPanel />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => close()}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
